/**
 * composerWarning — which single standing warning the composer shows.
 *
 * The composer used to stack up to three notice lines at once under the message
 * box (live-confirmed): the agent-pause hint, a blocked-channel line, and the
 * unknown-merge-token line, on top of whatever the attention strip was already
 * saying above it. The rule is now ONE. The generic "sending manually pauses the
 * AI agent" sentence stops being a line at all — it moves into the textarea's
 * placeholder, where it is read at the moment it matters (as you start typing)
 * and costs no vertical space.
 *
 * The priority below is about consequence, not tidiness:
 *  1. `draft_pending` — the agent has a written reply waiting right above and
 *     sending manually abandons it. Kept verbatim: it is the line that stops a
 *     recruiter stranding an approved answer to the lead.
 *  2. `channel_blocked` — this channel cannot send at all (opted out / no phone
 *     / no email), so the composer silently fell back to Note.
 *  3. `unknown_tokens` — a mistyped {{token}} would send blank.
 *
 * Pure and exported so the rules are tested without mounting the composer.
 */

export type ComposerWarningKind = 'draft_pending' | 'channel_blocked' | 'unknown_tokens' | null;

export interface ComposerWarningInput {
  /** The texting agent has an ACTIVE conversation on this thread. */
  agentActive: boolean;
  /** A copilot draft is sitting in the strip above, awaiting approval. */
  agentDraftPending: boolean;
  /** The mode the recruiter PICKED — what the blocked-channel lines key off,
   *  because a blocked pick is exactly the case where it is not in effect. */
  mode: 'sms' | 'email' | 'note';
  /** The mode actually in effect (a blocked sms/email pick falls back to note). */
  effectiveMode: 'sms' | 'email' | 'note';
  smsDisabled: boolean;
  emailDisabled: boolean;
  /** Token-shaped mistypes in the draft ({{study}}); already 0 in note mode. */
  unknownTokenCount: number;
}

/** The one warning the composer renders, or null for none. */
export function pickComposerWarning(i: ComposerWarningInput): ComposerWarningKind {
  // Notes are internal — they neither pause the agent nor strand its draft.
  if (i.agentActive && i.agentDraftPending && i.effectiveMode !== 'note') return 'draft_pending';
  if ((i.smsDisabled && i.mode === 'sms') || (i.emailDisabled && i.mode === 'email')) return 'channel_blocked';
  if (i.unknownTokenCount > 0) return 'unknown_tokens';
  return null;
}
